<script setup lang="ts" generic="T, N">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import fr from 'libs/shared/locales/fr.json';
import nl from 'libs/shared/locales/nl.json';
import en from 'libs/shared/locales/en.json';
import { NavButton } from '@precon-tools-suite-by-shared/shared-common-buttons-ui';
import {
    AbilityContext,
    AbilityGeneratorResult,
} from '@precon-tools-suite-by-shared/shared-auth-js-ability-checks';
import { DataTableRowEditSaveEvent } from 'primevue/datatable';
import DataTable from 'primevue/datatable';

export type PaginatedTableProps<T, N> = {
    items: T[];
    first?: number;
    dataKey?: string;
    pendingCountTillTarget?: number;
    useEdges?: boolean;
    rowsPerPageOptions?: number[];
    loading?: boolean;
    polling?: boolean;
    selectionMode?: 'single' | 'multiple';
    selection?: T | T[] | null | undefined;
    loadingMessage?: string;
    emptyMessage?: string;
    fetchingMessage?: string;
    pollingMessage?: string;
    navigatePath?: string;
    rowGroupMode?: 'subheader' | 'rowspan';
    groupRowsBy?: string | string[];
    scrollHeight?: string;
    header?: boolean;
    name?: string;
    selectionColumn?: boolean;
    edit?: {
        onRowEditSave: (event: DataTableRowEditSaveEvent) => Promise<void>;
        canEdit?:
            | boolean
            | (() => boolean)
            | ((ctx: AbilityContext, node?: N) => AbilityGeneratorResult);
        editMode?: 'row' | 'cell';
    };
    create?: {
        onCreate: (data: T) => void;
        canCreate?:
            | boolean
            | (() => boolean)
            | ((ctx: AbilityContext, node?: N) => AbilityGeneratorResult);
        name?: string;
    };
    update?: {
        onUpdate: (data: T) => void;
        canUpdate?:
            | boolean
            | (() => boolean)
            | ((ctx: AbilityContext, node?: N) => AbilityGeneratorResult);
    };
    delete?: {
        onDelete: (data: T) => void | Promise<void>;
        canDelete?:
            | boolean
            | (() => boolean)
            | ((ctx: AbilityContext, node?: N) => AbilityGeneratorResult);
    };
};

// ------------------------------------------------------------------------------------------------------------------ //
//  SETTINGS                                                                                                          //
// ------------------------------------------------------------------------------------------------------------------ //

const { t } = useI18n({
    locale: 'nl',
    fallbackLocale: 'en',
    messages: {
        en,
        nl,
        fr,
    },
});

const props = withDefaults(defineProps<PaginatedTableProps<T, N>>(), {
    first: 0,
    teamId: undefined,
    dataKey: '',
    pendingCountTillTarget: 0,
    rowsPerPageOptions: () => [10, 20, 50, 100],
    selectionMode: 'single',
    selection: null,
    loadingMessage: 'loadingMessage',
    emptyMessage: 'emptyMessage',
    fetchingMessage: 'fetchingMessage',
    pollingMessage: 'pollingMessage',
    navigatePath: undefined,
    rowGroupMode: undefined,
    groupRowsBy: undefined,
    scrollHeight: '',
    name: '',
    edit: () => ({
        onRowEditSave: () => Promise.resolve(),
    }),
    create: () => ({
        onCreate: () => {},
    }),
    update: () => ({
        onUpdate: () => {},
    }),
    delete: () => ({
        onDelete: () => {},
    }),
});

const emit = defineEmits([
    'update:selection',
    'update:loading',
    'update:total-count',
    'update:first',
    'update:rows',
]);

// ------------------------------------------------------------------------------------------------------------------ //
//  STATE                                                                                                             //
// ------------------------------------------------------------------------------------------------------------------ //

const first = ref<number>(props.first);
const polling = ref<boolean>(props.polling);
const selection = ref<T | T[] | null | undefined>(props.selection);
const selectionMode = ref<'single' | 'multiple' | undefined>(
    props.selectionMode
);

// ------------------------------------------------------------------------------------------------------------------ //
//  ACTIONS                                                                                                           //
// ------------------------------------------------------------------------------------------------------------------ //

const onUpdateFirst = () => {
    document.querySelector('.p-datatable-wrapper')?.scrollTo(0, 0);
    emit('update:first', first.value);
};

function onSelectionChange(newSelection) {
    emit('update:selection', newSelection);
}

function onLoadingChange(newLoading) {
    emit('update:loading', newLoading);
}

function onUpdateTotalCount(newTotalCount) {
    emit('update:total-count', newTotalCount);
}

function onUpdateRows(newRows) {
    emit('update:rows', newRows);
}
</script>
<template>
    <DataTable
        v-model:first="first"
        v-model:selection="selection"
        :value="items"
        :data-key="dataKey"
        class="fitted-table"
        scrollable
        scroll-direction="vertical"
        :scroll-height="scrollHeight"
        :rows-per-page-options="rowsPerPageOptions"
        :row-group-mode="rowGroupMode"
        :group-rows-by="groupRowsBy"
        :loading="loading"
        paginator
        :selection-mode="selectionMode"
        :edit-mode="edit?.editMode"
        paginator-template="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        current-page-report-template="{first} tot {last} van {totalRecords}"
        @update:first="onUpdateFirst"
        @update:selection="onSelectionChange"
        @update:loading="onLoadingChange"
        @update:total-count="onUpdateTotalCount"
        @update:rows="onUpdateRows"
        @row-edit-save="edit?.onRowEditSave"
    >
        <Column
            v-if="selectionColumn"
            selection-mode="multiple"
            style="width: 3rem"
        />
        <Column v-if="props.delete.canDelete" style="max-width: 2rem">
            <template #body="{ data }">
                <AbilityGate
                    :ability="props.delete.canDelete"
                    :parameters="[data]"
                    :hide-locked="true"
                >
                    <Button
                        :loading="data.__loading"
                        icon="pi pi-trash"
                        text
                        rounded
                        @click="props.delete.onDelete(data)"
                    />
                </AbilityGate>
            </template>
        </Column>
        <template v-if="header" #header>
            <slot name="header" />
        </template>
        <template #groupheader="{ data }">
            <slot name="groupheader" :data="data" />
        </template>
        <template #groupfooter="{ data }">
            <slot name="groupfooter" :data="data" />
        </template>
        <template #expansion="{ data }">
            <slot name="expansion" :data="data" />
        </template>
        <template #empty>
            <template v-if="!loading && !polling">
                <div class="flex flex-column align-items-center">
                    <i class="pi pi-search text-4xl mb-3" />
                    <span>{{
                        t(emptyMessage, {
                            typename: t(name).toLocaleLowerCase(),
                        })
                    }}</span>
                </div>
            </template>
            <template v-else>
                <Skeleton />
            </template>
        </template>
        <template #loading>
            <div class="flex flex-column align-items-center text-center">
                <i class="pi pi-spin pi-spinner text-4xl mb-3" />
                <template v-if="loading">
                    <span v-if="name && items.length > 0">{{
                        t(fetchingMessage, {
                            typename: t(name).toLocaleLowerCase(),
                            pendingCountTillTarget:
                                props.pendingCountTillTarget,
                        })
                    }}</span>
                    <span v-else>{{ t(loadingMessage) }}</span>
                </template>
                <template v-if="polling">
                    <span>{{
                        t(pollingMessage, {
                            typename: t(name).toLocaleLowerCase(),
                        })
                    }}</span>
                </template>
            </div>
        </template>
        <slot name="columns" />
        <Column
            v-if="props.edit.canEdit"
            :row-editor="true"
            style="width: 10%; min-width: 8rem"
            class="edit text-center text-primary"
        >
            <template #body="{ data, editorInitCallback }">
                <AbilityGate
                    :ability="props.edit.canEdit"
                    :parameters="[data]"
                    :hide-locked="true"
                >
                    <Button
                        v-tooltip.bottom="t('edit')"
                        class="p-button-primary p-button-rounded"
                        :loading="data.__loading"
                        icon="pi pi-pencil"
                        text
                        @click="(event: MouseEvent) => editorInitCallback(event)"
                    />
                </AbilityGate>
            </template>
            <template
                #editor="{ data, editorSaveCallback, editorCancelCallback }"
            >
                <!-- TODO: Something with the next click event goes competely wrong! -->
                <Button
                    v-tooltip.bottom="t('save')"
                    class="p-button-primary p-button-rounded"
                    :loading="data.__loading"
                    icon="pi pi-check"
                    text
                    @click="(event: MouseEvent) => props.edit.onRowEditSave(editorSaveCallback(event) as any)"
                />
                <Button
                    v-tooltip.bottom="t('cancel')"
                    class="p-button-secondary p-button-rounded"
                    :loading="data.__loading"
                    icon="pi pi-times"
                    text
                    @click="(event: MouseEvent) => editorCancelCallback(event)"
                />
            </template>
        </Column>

        <Column
            v-if="
                navigatePath || props.update.canUpdate || props.create.canCreate
            "
            field="actions"
        >
            <template #body="{ data }">
                <AbilityGate
                    :ability="props.update.canUpdate"
                    :parameters="[data]"
                    :hide-locked="true"
                >
                    <Button
                        v-if="props.update.canUpdate"
                        v-tooltip.bottom="t('update')"
                        class="p-button-primary p-button-rounded"
                        :loading="data.__loading"
                        icon="pi pi-pencil"
                        text
                        @click="props.update.onUpdate(data)"
                    />
                </AbilityGate>
                <AbilityGate
                    :ability="props.create.canCreate"
                    :parameters="[data]"
                    :hide-locked="true"
                >
                    <Button
                        v-if="props.create.canCreate"
                        v-tooltip.bottom="props.create.name"
                        class="p-button-primary p-button-rounded"
                        :loading="data.__loading"
                        icon="pi pi-plus"
                        text
                        @click="props.create.onCreate(data)"
                    />
                </AbilityGate>
                <NavButton
                    v-if="navigatePath"
                    :loading="data.__loading"
                    :disabled="data.__loading"
                    text
                    :tooltip-helper="t('view')"
                    :path="{
                        name: navigatePath,
                        params: {
                            id: useEdges ? data?.node?.id : data?.id,
                        },
                    }"
                />
            </template>
        </Column>
    </DataTable>
</template>

<i18n>
    {
        "en": {
            "loadingMessage": "Loading...",
            "fetchingMessage": "Fetching the next {pendingCountTillTarget} {typename}...",
            "pollingMessage": "Polling the {typename}...",
            "emptyMessage": "No {typename} found",
            "view": "View",
            "update": "Update",
            "create": "Add {name}",
            "edit": "Edit",
            "cancel": "Cancel",
            "save": "Save",
        },
        "nl": {
            "loadingMessage": "Aan het laden...",
            "fetchingMessage": "{pendingCountTillTarget} {typename} aan het laden...",
            "pollingMessage": "{typename} aan het pollen...",
            "emptyMessage": "Geen {typename} gevonden",
            "view": "Bekijk",
            "update": "Bewerk",
            "create": "Voeg {name} toe",
            "edit": "Bewerk",
            "cancel": "Annuleer",
            "save": "Opslaan",
        },
        "fr": {
            "loadingMessage": "Chargement...",
            "fetchingMessage": "Chargement des {pendingCountTillTarget} {typename} suivants...",
            "emptyMessage": "Aucun {typename} trouvé",
            "pollingMessage": "Chargement {typename}...",
            "view": "Vue",
            "update": "Modifier",
            "create": "Ajouter {name}",
            "edit": "Modifier",
            "cancel": "Annuler",
            "save": "Sauvegarder",
        }
    }
</i18n>
